/**
 * Impresión de tickets: Chrome kiosk (--kiosk-printing) o agente local Printify.
 * El motor se elige en la config del ticket del negocio (`motor_impresion`).
 */
import {
    buildTicketPrintPackage,
    buildFiscalTicketPrintPackage,
    buildSampleVenta,
    buildComandaPrintPackage,
} from '@/utils/ticketBuilder';
import {
    isPrintifyMotor,
    printifyPrint,
    PRINTIFY_DESTINOS,
} from '@/utils/printify';

const OFFICE_TICKET_WIDTH_MM = 110;

function pageWidthMm(profile) {
    return profile && profile.kind === 'thermal' ? profile.paperMm : OFFICE_TICKET_WIDTH_MM;
}

function buildPrintDocument(html, profile) {
    const widthMm = pageWidthMm(profile);
    return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Ticket</title>
<style>
@page { size: ${widthMm}mm auto; margin: 0; }
html, body { margin: 0; padding: 0; }
@media print {
  html, body { width: ${widthMm}mm; }
}
</style>
</head>
<body>${html}</body>
</html>`;
}

function printHtmlInHiddenFrame(html, profile) {
    return new Promise((resolve, reject) => {
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
        document.body.appendChild(iframe);

        let settled = false;
        const finish = (ok, err) => {
            if (settled) return;
            settled = true;
            window.removeEventListener('afterprint', onAfterPrint);
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            if (ok) resolve(true);
            else reject(err || new Error('No se pudo imprimir.'));
        };

        const onAfterPrint = () => finish(true);

        iframe.onload = () => {
            try {
                const win = iframe.contentWindow;
                if (!win || typeof win.print !== 'function') {
                    finish(false, Object.assign(new Error('Impresión no disponible.'), { code: 'NO_PRINT' }));
                    return;
                }
                window.addEventListener('afterprint', onAfterPrint, { once: true });
                win.focus();
                win.print();
                setTimeout(() => finish(true), 4000);
            } catch (e) {
                finish(false, e);
            }
        };

        iframe.srcdoc = buildPrintDocument(html, profile);
    });
}

export function canPrintTickets() {
    return typeof window !== 'undefined' && typeof window.print === 'function';
}

export function kioskPrintingHint() {
    return 'Abrí el POS con Chrome en modo kiosk y el flag --kiosk-printing. Configurá la impresora térmica como predeterminada en Windows.';
}

export function printifyPrintingHint() {
    return 'Abrí Printify en esta PC y asigná impresoras (ticket / comanda) en su ventana. El POS envía el HTML a http://127.0.0.1:18765.';
}

export function printingHint(ticketCfg) {
    return isPrintifyMotor(ticketCfg) ? printifyPrintingHint() : kioskPrintingHint();
}

export function chromeKioskLaunchHint() {
    return 'Ejemplo: chrome.exe --kiosk --kiosk-printing --app=https://tu-dominio/u/pos';
}

export async function printTicketPackage(printPackage, { ticketCfg, destino } = {}) {
    if (!printPackage || !printPackage.html) {
        throw Object.assign(new Error('No se pudo generar el ticket.'), { code: 'NO_TICKET' });
    }

    const dest = destino || PRINTIFY_DESTINOS.ticket;
    const htmlDoc = buildPrintDocument(printPackage.html, printPackage.profile);
    const widthMm = pageWidthMm(printPackage.profile);

    if (isPrintifyMotor(ticketCfg)) {
        await printifyPrint({
            destino: dest,
            html: htmlDoc,
            widthMm,
            jobName: `Puestify ${dest}`,
        });
        return { ok: true, via: 'printify', profile: printPackage.profile };
    }

    if (!canPrintTickets()) {
        throw Object.assign(new Error('Impresión no disponible en este navegador.'), { code: 'NO_PRINT' });
    }
    await printHtmlInHiddenFrame(printPackage.html, printPackage.profile);
    return { ok: true, via: 'kiosk', profile: printPackage.profile };
}

export async function printSaleTicket({ venta, ticketCfg, negocio, arca }) {
    const printPackage = buildTicketPrintPackage({ venta, ticketCfg, negocio, arca });
    return printTicketPackage(printPackage, { ticketCfg, destino: PRINTIFY_DESTINOS.ticket });
}

export async function printComandaTicket({ mesaNombre, items, cubiertos, vendedor, ticketCfg }) {
    const printPackage = buildComandaPrintPackage({ mesaNombre, items, cubiertos, vendedor, ticketCfg });
    return printTicketPackage(printPackage, { ticketCfg, destino: PRINTIFY_DESTINOS.comanda });
}

export async function printFiscalSaleTicket({ venta, ticketCfg, negocio, arca, comprobante }) {
    const printPackage = buildFiscalTicketPrintPackage({
        venta,
        ticketCfg,
        negocio,
        arca,
        comprobante,
    });
    return printTicketPackage(printPackage, { ticketCfg, destino: PRINTIFY_DESTINOS.fiscal });
}

export async function printSampleTicket({ ticketCfg, negocio, negocioNombre, arca }) {
    const n = { ...(negocio || {}) };
    if (!n.nombre) n.nombre = negocioNombre || 'Tu negocio';
    return printSaleTicket({ venta: buildSampleVenta(), ticketCfg, negocio: n, arca });
}
